import { Auth } from 'Shared/auth';
import { MediaTarget } from './target';
import { MediaItem } from './item';
import { acceptsSupportsMediaManagerV2 } from './media_manager_v2_support';
import MediaItemMenu from './item_menu';
import MediaLibrary from './library';
import '../../../3P/javascript-state-machine/state-machine';

onDomReady(() => {
   MediaManager.initialize();
});

/**
 * See docs in the README for this directory
 */

export const MediaManager = (function () {
   let mediaItemMenu;
   let attachedItems = {};

   MediaItem.addEvent('toggleMenu', (mediaItem, menuAnchor) => {
      mediaItemMenu.toggleForMediaItem(mediaItem, menuAnchor);
   });

   function setupImageMarkersDialog() {
      import('Shared/modal').then(({ Modal }) => {
         Modal.addEvents({
            onImageMarkersLoad: function (contentBox, options, responseData) {
               ImageMarkers.initialize(contentBox, options, responseData);
            },
            onImageMarkersUnload: function () {
               ImageMarkers.unload();
            },
         });
      });
   }

   let publicInterface = {
      // holds the mediaTarget that is being altered
      mediaTarget: null,

      initialize: function () {
         MediaTarget.registerAllTargets();

         setupMediaItemMenu();
      },

      // When a mediaItem is selected from Media Manager, it is appended to
      // `attachedItems`. When it is `detach`ed, it goes back into the list of
      // media.
      detach: function (mediaItem) {
         Auth.required({
            onAuthorize: function () {
               delete attachedItems[mediaItem.getGlobalID()];
               mediaItem.deleted();
            },
         });
      },

      /**
       * Called when we enter the choosing state
       */
      onenterchoosing: function (event, from, to, mediaTarget) {
         // If the user cancelled the crop dialog, Modal.js will take
         // care of showing the Library. Effectively repeating this comand
         if (from != 'hidden') {
            return;
         }

         MediaLibrary.showForTarget(mediaTarget, complete, attachedItems);

         function complete(mediaItem) {
            if (!mediaItem) {
               return MediaManager.cancel();
            }

            let filter_state = mediaItem.data.filter_state();
            // see MediaFilters.php for what these values mean
            switch (filter_state) {
               case 'crop': {
                  MediaManager.crop(mediaItem, mediaTarget);
                  break;
               }
               // If true, it meets all the requirements; Attach it.
               case true:
               // If WRONG_TYPE, let the target reject it with a custom error
               // message
               // falls through
               case false: {
                  MediaManager.select(mediaItem, mediaTarget);
                  break;
               }

               default: {
                  // If the image is still uploading, the filter_state will be
                  // undefined.
                  if (filter_state) {
                     MediaLibrary.appendAlertMessage(filter_state.message);
                  }
                  break;
               }
            }
         }
      },

      oncrop: function (event, from, to, mediaItem, mediaTarget) {
         ImageCrop.showForMediaItem(mediaItem, mediaTarget, function complete(mediaItem) {
            if (mediaItem) {
               MediaManager.select(mediaItem, mediaTarget);
               // Pop the media library off the modal stack too
               // so we go straight back to the page.
               import('Shared/modal').then(({ Modal }) => {
                  Modal.pop();
               });
            } else {
               MediaManager.cancelCrop();
            }
         });
      },

      /**
       * Called before transitioning via the 'select' method. Gives the target
       * an opportunity to veto the selection or perform it's own actions.
       *
       * If ASYNC state transitions were enabled in onbeforeselect() I would
       * have done that... but they only work for onleave* functions.
       */
      onleavechoosing: validateSelection,
      onleavecropping: validateSelection,

      onenterhidden: function () {
         import('Shared/modal').then(({ Modal }) => {
            Modal.pop();
         });
      },

      /**
       * Open the React Media Manager v2 for a target instead of the legacy
       * MooTools library. v2 renders its own React modal (not a legacy Modal),
       * so we deliberately skip the state machine — whose onenterhidden pops
       * the legacy Modal — and run the target's attach protocol directly,
       * mirroring the bookkeeping in validateSelection(). v2 only resolves
       * items it has already deemed attachable, so there's no crop/veto branch
       * to replicate here.
       */
      _browseV2: function (mediaTarget) {
         // Match the legacy path (MediaLibrary.showForTarget), which gates the
         // modal behind login; without this an expired session opens the modal
         // onto a wall of 401s from the library endpoints.
         if (!Auth.isLoggedIn()) {
            Auth.required({
               onAuthorize: () => MediaManager._browseV2(mediaTarget),
            });
            return;
         }

         let attach = function (mediaItem) {
            mediaTarget.attachItem(mediaItem, (oldMediaItem, success, errorMessage) => {
               if (!success) {
                  // The modal is already closed, so surface the veto the same
                  // way the customAttach targets do.
                  if (errorMessage) {
                     alert(errorMessage);
                  }
                  return;
               }
               if (oldMediaItem) {
                  delete attachedItems[oldMediaItem.getGlobalID()];
               }
               mediaItem.whenReady(() => {
                  attachedItems[mediaItem.getGlobalID()] = true;
               });
            });
         };

         // Open a legacy edit dialog for a v2 tile. The React modal has already
         // been unmounted by openMediaManager (its Radix layer would otherwise
         // freeze these MooTools dialogs), so they're clickable. Returns
         // { closed, saved }: `closed` reopens the modal, `saved` fires when the
         // server write lands so the tile stops spinning and shows the result.
         let editItem = (item, action) => {
            let resolveClosed;
            let resolveSaved;
            let closed = new Promise(resolve => {
               resolveClosed = resolve;
            });
            let saved = new Promise(resolve => {
               resolveSaved = resolve;
            });

            if (action === 'crop') {
               // The crop dialog closes when the crop *request* is accepted;
               // the image itself comes from a background job, and the item's
               // dataPromise resolves once cropImageStatus reports it. On
               // cancel no item is passed and there's nothing to wait for.
               ImageCrop.showForMediaItem(MediaItem.createFromData(item), mediaTarget, cropped => {
                  resolveClosed();
                  if (cropped) {
                     cropped.whenReady(() => resolveSaved());
                  } else {
                     resolveSaved();
                  }
               });
            } else {
               // The markers dialog loads its canvas source via
               // getSrc('medium'), but the library item only carries the sizes
               // stored as renditions and often omits medium. Derive it from
               // another size (the image server makes any size on demand);
               // without a source the fabric canvas never inits and the dialog's
               // key handlers throw.
               let source = item.medium || item.standard || item.original || item.thumbnail;
               // Swap only a real extension after the last dot in the file
               // name; a dotless URL passes through unchanged rather than
               // letting the pattern eat into the hostname.
               let withMedium = item.medium
                  ? item
                  : { ...item, medium: source && source.replace(/\.[^./]+$/, '.medium') };
               let mediaItem = MediaItem.createFromData(withMedium);
               import('Shared/modal').then(({ Modal }) => {
                  Modal.open({
                     type: 'module',
                     name: 'ImageMarkers',
                     boxClass: 'mediaLibraryModalBox editPhoto',
                     clientOptions: { mediaImage: mediaItem },
                     serverOptions: { imageid: mediaItem.getID() },
                     // Markers pops the dialog before its save POST (which makes
                     // a *new* image) finishes: reopen on close, and whenReady
                     // resolves when the write lands (or now, on cancel).
                     onClose: () => {
                        resolveClosed();
                        mediaItem.whenReady(() => resolveSaved());
                     },
                  });
               });
            }

            return { closed, saved };
         };

         import('Shared/MediaManager').then(({ openMediaManager }) => {
            openMediaManager({
               accepts: mediaTarget.getMediaFilterName(),
               hiddenGlobalIds: Object.keys(attachedItems),
               editItem: editItem,
            }).then(item => {
               if (!item) {
                  return;
               }
               let mediaItem = MediaItem.createFromData(item);
               // A crop-required image: the modal has closed, so the legacy
               // cropperjs dialog opens clickable; attach the cropped result.
               if (item.filter_state === 'crop') {
                  ImageCrop.showForMediaItem(mediaItem, mediaTarget, cropped => {
                     if (cropped) {
                        // Wait for the crop job before attaching: the loading
                        // item's filter_state is stripped, and strict targets
                        // (guide edit's customAttach) veto anything that isn't
                        // explicitly passing. The resolved data carries the
                        // server-computed state for this target's filter.
                        cropped.whenReady(() => attach(cropped));
                     } else {
                        // Cancelled: v1 fell back to the still-open library;
                        // ours is unmounted, so reopen it instead of leaving
                        // the user stranded on the page.
                        MediaManager._browseV2(mediaTarget);
                     }
                  });
               } else {
                  attach(mediaItem);
               }
            });
         });
      },
   };

   /**
    * If we are performing the 'select' event,
    * Attach the item to the target and give it the option to veto the
    * attachment.
    */
   function validateSelection(event, from, to, mediaItem, mediaTarget) {
      if (event !== 'select') {
         return true;
      }
      let mediaManager = this;
      mediaTarget.attachItem(mediaItem, (oldMediaItem, success, msg) => {
         if (!success) {
            if (msg) {
               MediaLibrary.appendAlertMessage(msg);
            }
            return mediaManager.transition.cancel();
         }

         if (oldMediaItem) {
            delete attachedItems[oldMediaItem.getGlobalID()];
         }

         /**
          * If the media item requires cropping, it will have a different Id
          * when it's done. So, we'll wait for that.
          */
         mediaItem.whenReady(() => {
            attachedItems[mediaItem.getGlobalID()] = true;
         });
         mediaManager.transition();
      });

      // This transition will be asyncronous cause we need to wait on the
      // above callback to verify success.
      return StateMachine.ASYNC;
   }

   /**
    * Setup event handlers for the MediaItemMenu
    *
    * For most of these actions, we don't really care about completion / cancel
    * events cause dialogs do Modal.pop() and leave us right where we started.
    */
   function setupMediaItemMenu() {
      let self = this;
      mediaItemMenu = new MediaItemMenu();
      let menuItems = mediaItemMenu.menuItems;

      setupImageMarkersDialog(menuItems);

      menuItems.addEvents({
         markers: function (mediaItem) {
            Auth.required({
               onAuthorize: function () {
                  let clientOptions = { mediaImage: mediaItem };
                  import('Shared/modal').then(({ Modal }) => {
                     Modal.open({
                        type: 'module',
                        name: 'ImageMarkers',
                        boxClass: 'mediaLibraryModalBox editPhoto',
                        clientOptions: clientOptions,
                        serverOptions: {
                           imageid: mediaItem.getID(),
                        },
                     });
                  });
               },
            });
         },
         crop: function (mediaItem) {
            Auth.required({
               onAuthorize: function () {
                  let itemContext = mediaItem.getContext();
                  let mediaTarget = null;

                  // If the mediaItem is in the media manager, we still need to
                  // have the crop dialog aware of the mediaTarget the library is
                  // open for so it can get the filter state (pass, too small, needs
                  // crop, ...) after cropping and enforce the appropriate aspect
                  // ratio.
                  if (itemContext == MediaLibrary) {
                     mediaTarget = MediaLibrary.mediaTarget;
                     // If the mediaItem is attached to a target, make sure to use that
                     // as the context for the filter.
                  } else if (instanceOf(itemContext, MediaTarget)) {
                     mediaTarget = itemContext;
                  }

                  ImageCrop.showForMediaItem(mediaItem, mediaTarget);
               },
            });
         },
         deleted: function (mediaItem) {
            publicInterface.detach(mediaItem);
         },
         fullsize: function (mediaItem) {
            // Get image source URL, remove '.thumbnail' suffix, and open
            // it in a new window.
            let imageURL = mediaItem.data.original();
            window.open(imageURL);
         },
         copy: function (mediaItem) {
            Auth.required({
               onAuthorize: mediaItem.copyToMediaLibrary.bind(mediaItem),
            });
         },
      });
   }

   return publicInterface;
})();

/**
 * Taken from: https://github.com/jakesgordon/javascript-state-machine
 */
StateMachine.create({
   target: MediaManager,
   initial: 'hidden',

   events: [
      // cancelling is allowed from anywhere
      { name: 'cancel', from: '*', to: 'hidden' },
      { name: 'cancelCrop', from: 'cropping', to: 'choosing' },

      // Triggered by clicking a mediaTarget
      { name: 'browse', from: 'hidden', to: 'choosing' },
      { name: 'crop', from: 'choosing', to: 'cropping' },
      { name: 'select', from: ['choosing', 'cropping'], to: 'hidden' },
   ],
});

// Route supported media-manager attachment points through Media Manager v2 when
// the flag is on. This is the single chokepoint all targets funnel through
// (both MediaTarget's browse event and direct MinimalMediaTarget callers), so
// wiring it here means v2 works anywhere a supported media manager is attached,
// without each consumer opting in. Falls back to the legacy state-machine flow
// otherwise.
const legacyBrowse = MediaManager.browse;
MediaManager.browse = function (mediaTarget) {
   if (
      typeof App !== 'undefined' &&
      App.mediaManagerV2 &&
      acceptsSupportsMediaManagerV2(mediaTarget.getMediaFilterName())
   ) {
      MediaManager._browseV2(mediaTarget);
      return;
   }
   return legacyBrowse.call(MediaManager, mediaTarget);
};
